import type { ModelExpenseArticle } from '@app/api/ExpenseArticles';
import type { ModelPaymentAccount } from '@app/api/PaymentAccounts';
import type { COLLECTIONS, EXPENSES, PAYMENTS } from '@app/constants';
import type { NClientPaymentsList } from '@app/pages/Clients/ClientPayments/ClientPaymentsList/ClientPaymentsList.types';
import type { NRecords, NSmallGlobalEntities } from '@app/types';
import type { NClients } from '@app/types/Clients';
import type { NClientServices } from '@app/types/ClientServices';
import type { NLegalEntities } from '@app/types/LegalEntities';
import type { NUsers } from '@app/types/Users';

import type { FormInstance } from 'antd';
import type {
  FO_PAY_BANK,
  FO_PAY_CASH,
} from 'src/pages/Finances/FinancialOperations/constants/payTypes';

export namespace NClientPayments {
  export type ModelPaymentExpenseInfo = {
    ObjectId: number | null;
    Timestamp: string;
    IsPayment: boolean;
    IsCollection: boolean;
    UserId: number;
    User: string | null;
    ClientId: number | null;
    Client: string | null;
    ClientPhones: string | null;
    Amount: number;
    PayType: PayType | null;
    PaymentAccountId: number | null;
    PaymentAccount: string | null;
    LegalEntityId: number | null;
    LegalEntity: string | null;
    ClientRecords: ModelRecordInfo[] | null;
    Comment: string | null;
    ExpenseArticleId: number;
    ExpenseArticle: string | null;
    Attachments: ModelFileDescription[] | null;
  };

  export type ModelFileDescription = {
    Id: number;
    FileName: string;
    FileSize: number;
    Description: string;
    Extension: string;
    Thumbnail: string;
  };

  export type ModelFile = {
    Id: number;
    Name: string;
    Data: string;
    Lifetime: string;
    Description: string;
  };

  export type ModelMoneyExpense = {
    Id: number;
    Timestamp: string;
    UserId: number;
    User: string | null;
    PaymentAccountId: number;
    PaymentAccount: string | null;
    ExpenseArticleId: number;
    ExpenseArticle: string | null;
    Amount: number;
    Comment: string | null;
  };

  export type ModelRecordInfo = {
    Id: number;
    TimeS: string;
    TimeE: string;
    EmployeeId: number;
    NameFirst: string;
    NameLast: string;
    NameMiddle: string;
  };

  export type ModelClientPaymentFiltersData = {
    Users: NSmallGlobalEntities.ModelItem[] | null;
    PaymentAccounts: NSmallGlobalEntities.ModelItem[] | null;
    LegalEntities: NSmallGlobalEntities.ModelItem[] | null;
    ExpenseArticles: NSmallGlobalEntities.ModelItem[] | null;
  };

  export type AddingModal = (args: {
    title: string;
    isModalOpen: boolean;
    setIsModalOpen: (arg: boolean) => void;
    form: FormInstance;
    paymentAccounts: ModelPaymentAccount[];
    className: string;
    isExpense?: boolean;
    isCollection?: boolean;
    expenseArticles?: ModelExpenseArticle[];
    submit?: NClientPaymentsList.GeneralFunctionsResult;
    isModalLoading: boolean;
    selectedPaymentAccountId: number;
    setSelectedPaymentAccountId: (value: number) => void;
  }) => JSX.Element;

  export type ModelOperationsFiltersData = ModelClientPaymentFiltersData;

  export type ModelOperationSearchFilter = {
    SearchTerm: string | null;
    DateS: string | null;
    DateE: string | null;
    ClientIds: Array<NClients.ClientLite['Id']> | null;
    UserIds: Array<NUsers.User['id']> | null;
    PaymentAccountIds: Array<ModelPaymentAccount['Id']> | null;
    LegalEntityIds: Array<NLegalEntities.ModelLegalEntity['Id']> | null;
    PayTypes: PayType[] | null;
    ItemsPerPage: number;
  };

  export type PayType = typeof FO_PAY_CASH | typeof FO_PAY_BANK;

  export type ModelOperationSearchResult = {
    Id: number;
    Filter: ModelOperationSearchFilter | null;
    Items: ModelOperation[] | null;
    PageN: number;
    CountPages: number;
    CountItems: number;
  };

  export type ModelOperation = {
    Id: number;
    Timestamp: string;
    UserId: NUsers.User['id'];
    UserName: string | null;
    RecordId: NRecords.ClientRecord['Id'];
    ClientServiceId: NClientServices.Service['Id'];
    ClientService: NClientServices.Service['Name'];
    ClientId: NClients.ClientLite['Id'];
    ClientName: NClients.ClientAbstract['Name'];
    PaymentAccountId: ModelPaymentAccount['Id'];
    PaymentAccount: ModelPaymentAccount['Name'];
    LegalEntityId: NLegalEntities.ModelLegalEntity['Id'];
    LegalEntity: NLegalEntities.ModelLegalEntity['Name'];
    PayType: PayType;
    Amount: number;
    ExpenseArticleId: number;
  };

  export type ModelPaymentSearchFilter = {
    SearchTerm: string | null;
    DateS: string | null;
    DateE: string | null;
    ClientIds: number[] | null;
    UserIds: number[] | null;
    PaymentAccountIds: number[] | null;
    LegalEntityIds: number[] | null;
    PayTypes: PayType[] | null;
    ItemsPerPage: number;
    Operations: Operation[] | null;
    ArticleIds: number[] | null;
    PaymentSystems: string[] | null;
  };

  export type Operation =
    | typeof PAYMENTS
    | typeof EXPENSES
    | typeof COLLECTIONS;

  export type ModelPaymentSearchResult = {
    Id: number;
    Filter: ModelPaymentSearchFilter;
    Items: ModelPaymentExpenseInfo[] | null;
    PageN: number;
    CountPages: number;
    CountItems: number;
  };

  export type ModelBalance = {
    ObjectId: number;
    Balance: number;
  };

  export type ModelTopClientBalance = {
    ClientId: number;
    PaymentAccountId: number;
    Amount: number;
    Comment: string | null;
  };

  export type ModelPaymentInsertion = {
    AccountId: number;
    Amount: number;
    Comment: string | null;
  };

  export type ModelClientPayment = {
    Id: number;
    TimeCreated: string;
    CreateUserId: number;
    ClientId: number;
    Amount: number;
    AmountLeft: number;
    PayType: PayType | null;
    PaymentAccountId: number;
    IsDeleted: boolean;
  };
}
